/** @jsxImportSource @emotion/react */

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { HTMLAttributes } from "react";

import { cssMediaQueryMinWidthTablet, Text } from "../../../emotion";
import type { ThemeType } from "../../../global/emotion";
import { formatCompactNumber } from "../../utils/numberFormat";
import { AppleIcon, GooglePlayIcon } from "../_base/icons/Icon/ExternalIcons";

const APPLE_RATING = 4.9;
const APPLE_RATINGS_COUNT = 322000;
const GOOGLE_PLAY_RATING = 4.7;
const GOOGLE_PLAY_RATINGS_COUNT = 122000;

const Star = () => (
  <svg fill="none" height="12" viewBox="0 0 13 12" width="13">
    <path
      clipRule="evenodd"
      d="M7.45709 0.88881L8.42646 2.80037L10.6088 3.10898C11.9007 3.29169 12.5229 4.91276 11.5017 5.88154L9.94438 7.35885L10.3104 9.43552C10.5567 10.8331 9.0867 11.7271 7.96598 11.1537L6.0059 10.1508L4.04582 11.1537C2.92509 11.7271 1.45514 10.8331 1.70144 9.43552L2.06742 7.35885L0.510144 5.88154C-0.511086 4.91275 0.111076 3.29169 1.40303 3.10898L3.58534 2.80037L4.55471 0.888809C5.15568 -0.296273 6.85612 -0.296266 7.45709 0.88881Z"
      fill="#FF9547"
      fillRule="evenodd"
    />
  </svg>
);

type RatingsProps = Omit<HTMLAttributes<HTMLDivElement>, "children">;

const cssRatingContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  justifyContent: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: theme.radius.m,
  borderStyle: "solid",
  borderWidth: theme.stroke.regular,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
  padding: theme.spacing.m,
  width: "100%",
});
const cssRatingsContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  gap: theme.spacing.s,
  width: "100%",
});
const cssRatingHeadlineContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.xxs,
});
const cssRatingText: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const cssRatingTextContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  justifyContent: "center",
});
const cssRatingTextDesktop: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: {
    display: "flex",
  },
};
const cssRatingTextMobile: Interpolation = {
  display: "flex",
  [cssMediaQueryMinWidthTablet]: {
    display: "none",
  },
};

export const Ratings = ({ className, ...props }: RatingsProps) => {
  const t = useTranslations();

  return (
    <div className={className} css={cssRatingsContainer} {...props}>
      <div css={cssRatingContainer}>
        <AppleIcon />
        <div css={cssRatingTextContainer}>
          <div css={cssRatingHeadlineContainer}>
            <Text size="m" type="headline">
              {APPLE_RATING}
            </Text>
            <Star />
          </div>
          <Text
            css={[cssRatingText, cssRatingTextDesktop]}
            size="m"
            type="body"
          >
            {t("general_words_ratings", {
              0: formatCompactNumber(APPLE_RATINGS_COUNT),
            })}
          </Text>
          <Text css={[cssRatingText, cssRatingTextMobile]} size="s" type="body">
            {t("general_words_ratings", {
              0: formatCompactNumber(APPLE_RATINGS_COUNT),
            })}
          </Text>
        </div>
      </div>
      <div css={cssRatingContainer}>
        <GooglePlayIcon />
        <div css={cssRatingTextContainer}>
          <div css={cssRatingHeadlineContainer}>
            <Text size="m" type="headline">
              {GOOGLE_PLAY_RATING}
            </Text>
            <Star />
          </div>
          <Text
            css={[cssRatingText, cssRatingTextDesktop]}
            size="m"
            type="body"
          >
            {t("general_words_ratings", {
              0: formatCompactNumber(GOOGLE_PLAY_RATINGS_COUNT),
            })}
          </Text>
          <Text css={[cssRatingText, cssRatingTextMobile]} size="s" type="body">
            {t("general_words_ratings", {
              0: formatCompactNumber(GOOGLE_PLAY_RATINGS_COUNT),
            })}
          </Text>
        </div>
      </div>
    </div>
  );
};
